import type { HeroAnimationFacing } from '@/modules/game-domain/render-snapshot';

export const BASE_IDLE_CLIPS = ['idle-bored', 'idle-breathing'] as const;
export const WIDE_IDLE_CLIPS = ['idle', 'idle-catwalk', 'idle-stretch'] as const;
export type IdleClip = typeof BASE_IDLE_CLIPS[number] | typeof WIDE_IDLE_CLIPS[number];
export interface IdlePresentation { clip: IdleClip; progress: number; }
export const IDLE_INTERVAL_MIN_MS = 20_000;
export const IDLE_INTERVAL_MAX_MS = 40_000;
export const IDLE_RESUME_GAP_MS = 5_000;
export type IdleDurationResolver = (variant: number, clip: IdleClip, facing: HeroAnimationFacing) => number | undefined;

/** Presentation clock only. The engine's RAF and the local gallery can share this scheduler. */
export class HeroIdleScheduler {
  private variant: number | null = null;
  private base: typeof BASE_IDLE_CLIPS[number] | null = null;
  private baseStartedAt = 0;
  private wide: { clip: typeof WIDE_IDLE_CLIPS[number]; startMs: number; durationMs: number } | null = null;
  private nextWideAt = 0;
  private previousSample: number | null = null;

  constructor(private resolveDuration: IdleDurationResolver, private random: () => number = Math.random) {}

  reset(): void {
    this.variant = null; this.base = null; this.wide = null; this.previousSample = null;
    this.baseStartedAt = 0; this.nextWideAt = 0;
  }

  private roll(): number {
    const value = this.random();
    return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
  }
  private interval(): number {
    return IDLE_INTERVAL_MIN_MS + this.roll() * (IDLE_INTERVAL_MAX_MS - IDLE_INTERVAL_MIN_MS);
  }

  sample(nowMs: number, variant: number, facing: HeroAnimationFacing): IdlePresentation | null {
    if (!Number.isFinite(nowMs)) return null;
    if (variant !== this.variant) this.reset();
    if (!this.base) {
      const base = BASE_IDLE_CLIPS[Math.min(BASE_IDLE_CLIPS.length - 1, Math.floor(this.roll() * BASE_IDLE_CLIPS.length))]!;
      const duration = this.resolveDuration(variant, base, facing);
      if (!duration || !Number.isFinite(duration) || duration <= 0) return null;
      this.variant = variant; this.base = base; this.baseStartedAt = nowMs;
      this.nextWideAt = nowMs + this.interval();
    } else if (this.previousSample !== null && (nowMs < this.previousSample || nowMs - this.previousSample > IDLE_RESUME_GAP_MS)) {
      // A resumed background tab starts a fresh quiet window, never a backlog of gestures.
      this.wide = null; this.baseStartedAt = nowMs; this.nextWideAt = nowMs + this.interval();
    }
    this.previousSample = nowMs;
    if (this.wide && nowMs - this.wide.startMs >= this.wide.durationMs) {
      this.wide = null; this.baseStartedAt = nowMs; this.nextWideAt = nowMs + this.interval();
    }
    if (!this.wide && nowMs >= this.nextWideAt) {
      const clip = WIDE_IDLE_CLIPS[Math.min(WIDE_IDLE_CLIPS.length - 1, Math.floor(this.roll() * WIDE_IDLE_CLIPS.length))]!;
      const duration = this.resolveDuration(variant, clip, facing);
      if (duration && Number.isFinite(duration) && duration > 0) this.wide = { clip, startMs: nowMs, durationMs: duration };
      else this.nextWideAt = nowMs + this.interval();
    }
    if (this.wide) return { clip: this.wide.clip, progress: Math.max(0, (nowMs - this.wide.startMs) / this.wide.durationMs) };
    const duration = this.resolveDuration(variant, this.base, facing);
    if (!duration || !Number.isFinite(duration) || duration <= 0) return null;
    return { clip: this.base, progress: (Math.max(0, nowMs - this.baseStartedAt) % duration) / duration };
  }
}
